// DashboardSelection.jsx (Enhanced version)
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { auth } from './firebase';
import { getFirestore, doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';

const firestore = getFirestore();

function DashboardSelection() {
    const [accounts, setAccounts] = useState([]);
    const [userEmail, setUserEmail] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();

    // Function to search for user accounts (same as in Auth.js)
    const findUserInAllCollections = async (userEmail) => {
        const foundAccounts = [];

        try {
            // Check 1: Investment/Trading users
            const investmentQuery = query(
                collection(firestore, "UserRegistrations"), 
                where("email", "==", userEmail)
            );
            const investmentSnapshot = await getDocs(investmentQuery);
            
            if (!investmentSnapshot.empty) {
                const userData = investmentSnapshot.docs[0].data();
                foundAccounts.push({
                    type: 'investment',
                    data: userData,
                    id: investmentSnapshot.docs[0].id,
                    title: 'Funds Management',
                    description: 'Access your trading dashboard and copy trading features',
                    icon: '📈',
                    route: userData.subscriptionPlan === 'trial' ? '/investdashboard' : 
                           (userData.status === 'active' || userData.backendRegistered) ? 
                           '/investdashboard' : '/USDTPayment'
                });
            }

            // Check 2: Training program users
            const trainingQuery = query(
                collection(firestore, "FormRegistration"), 
                where("emailAddress", "==", userEmail)
            );
            const trainingSnapshot = await getDocs(trainingQuery);
            
            if (!trainingSnapshot.empty) {
                const userData = trainingSnapshot.docs[0].data();
                foundAccounts.push({
                    type: 'training',
                    data: userData,
                    id: trainingSnapshot.docs[0].id,
                    title: 'Training Program',
                    description: 'Access your algorithmic trading course and materials',
                    icon: '🎓',
                    route: '/academy/dashboard'
                });
            }

            // Check 3: General investors
            const investorDoc = doc(firestore, "Investors", auth.currentUser?.uid || 'temp');
            const investorSnap = await getDoc(investorDoc);
            
            if (investorSnap.exists()) {
                const userData = investorSnap.data();
                foundAccounts.push({
                    type: 'general',
                    data: userData,
                    id: investorSnap.id,
                    title: 'General Investment',
                    description: 'Access your investment portfolio and tools',
                    icon: '💼',
                    route: '/userdash'
                });
            }

            return foundAccounts;
        } catch (error) {
            console.error("Error finding user accounts:", error);
            return [];
        }
    };

    useEffect(() => {
        const loadUserAccounts = async () => {
            setIsLoading(true);
            
            // First check if we have stored accounts
            const storedAccounts = localStorage.getItem('multipleAccounts');
            const storedEmail = localStorage.getItem('userEmail');
            
            if (storedAccounts && storedEmail) {
                setAccounts(JSON.parse(storedAccounts));
                setUserEmail(storedEmail);
                setIsLoading(false);
                return;
            }

            // If no stored accounts, check if user is logged in and search
            const currentUser = auth.currentUser;
            if (currentUser) {
                const foundAccounts = await findUserInAllCollections(currentUser.email);
                
                if (foundAccounts.length > 0) {
                    setAccounts(foundAccounts);
                    setUserEmail(currentUser.email);
                    
                    // Store for future use
                    localStorage.setItem('multipleAccounts', JSON.stringify(foundAccounts));
                    localStorage.setItem('userEmail', currentUser.email);
                } else {
                    // No accounts found, redirect to registration
                    navigate('/userdetails');
                    return;
                }
            } else {
                // Not logged in, redirect to auth
                navigate('/auth');
                return;
            }
            
            setIsLoading(false);
        };

        loadUserAccounts();
    }, [navigate]);

    const handleDashboardSelect = (account) => {
        // Set user type and data in localStorage
        localStorage.setItem('userType', account.type);
        localStorage.setItem('userId', account.data.userId || account.id);
        
        // Navigate to selected dashboard
        if (account.type === 'investment' && account.route === '/USDTPayment') {
            navigate('/USDTPayment', { 
                state: { 
                    plan: account.data.subscriptionPlan,
                    userId: account.data.userId,
                    registrationId: account.id
                }
            });
        } else if (account.type === 'training') {
            navigate('/academy/dashboard');
        } else {
            navigate(account.route);
        }
    };

    const handleSignOut = async () => {
        try {
            await auth.signOut();
            localStorage.clear();
            navigate('/auth');
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                    <p className="text-white text-lg">Loading your accounts...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 flex items-center justify-center p-4">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-4xl w-full"
            >
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-white mb-4">
                        Choose Your Dashboard
                    </h1>
                    <p className="text-gray-300 text-lg">
                        Welcome back <span className="text-blue-400 font-semibold">{userEmail}</span>!
                    </p>
                    <p className="text-gray-400 mt-2">
                        Select which service you'd like to access
                    </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                    {accounts.map((account, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1 }}
                            whileHover={{ scale: 1.05 }}
                            onClick={() => handleDashboardSelect(account)}
                            className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20 cursor-pointer hover:bg-white/20 transition-all duration-300"
                        >
                            <div className="text-center">
                                <div className="text-4xl mb-4">{account.icon}</div>
                                <h3 className="text-xl font-bold text-white mb-2">
                                    {account.title}
                                </h3>
                                <p className="text-gray-300 text-sm mb-4">
                                    {account.description}
                                </p>
                                
                                {/* Show account status/details */}
                                <div className="bg-black/30 rounded-lg p-3 mb-4">
                                    {account.type === 'investment' && (
                                        <div className="text-xs text-gray-300">
                                            <p>Plan: <span className="text-blue-400">{account.data.subscriptionPlan}</span></p>
                                            <p>Status: <span className="text-green-400">{account.data.status || 'Active'}</span></p>
                                        </div>
                                    )}
                                    {account.type === 'training' && (
                                        <div className="text-xs text-gray-300">
                                            <p>Program: <span className="text-purple-400">Algorithmic Trading</span></p>
                                            <p>Status: <span className="text-green-400">Enrolled</span></p>
                                        </div>
                                    )}
                                    {account.type === 'general' && (
                                        <div className="text-xs text-gray-300">
                                            <p>Type: <span className="text-yellow-400">Investment Portfolio</span></p>
                                            <p>Status: <span className="text-green-400">Active</span></p>
                                        </div>
                                    )}
                                </div>
                                
                                <button className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all">
                                    Access Dashboard
                                </button>
                            </div>
                        </motion.div>
                    ))}
                </div>

                <div className="text-center space-y-4">
                    <button
                        onClick={() => navigate('/')}
                        className="text-blue-400 hover:text-blue-300 underline text-sm mr-6"
                    >
                        ← Back to Home
                    </button>
                    
                    <button
                        onClick={handleSignOut}
                        className="text-red-400 hover:text-red-300 underline text-sm"
                    >
                        Sign out and use different account
                    </button>
                </div>
            </motion.div>
        </div>
    );
}

export default DashboardSelection;